import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { InvoiceService } from "../../../services/user/invoices.services";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";
import { getButtonStyles } from "../../../utility";
import moment from "moment";

const InvoiceComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [invoiceToDelete, setInvoiceToDelete] = useState(null);
    const rowsPerPage = 5;
    const [selectedStatusCard, setSelectedStatusCard] = useState(null);
    const [invoiceTitle, setInvoiceTitle] = useState('Invoice');
    const [invoiceTitleColor, setInvoiceTitleColor] = useState('')

    // Fetch data
    useEffect(() => {
        const fetchInvoiceData = async () => {
            try {
                setLoading(true);
                const response = await InvoiceService.getAllInvoices(selectedStore);
                setData(response);
            } catch (error) {
                handleNotify("Error fetching invoices", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchInvoiceData();
    }, [selectedStore]);


    const filterData = (data) => {
        return (
            data.filter(row =>
                (selectedStatusCard ? row.status?.statusName === selectedStatusCard : true) &&
                (
                    row.invoiceSubject?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.address?.city.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.phoneNumber.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.user?.username?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.items?.item?.itemName.toLowerCase().includes(searchText.toLowerCase()) ||
                    (row.status?.statusName?.toLowerCase().includes(searchText.toLowerCase()))
                )
            )
        )
    };

    // Handle pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const filteredData = filterData(data);
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };
    // Handle Edit
    const handleEditClick = (item) => {
        navigate('/invoice/edit', { state: { invoiceId: item?.invoiceId } });
    };
    // Handle Delete
    const handleDeleteClick = (invoice) => {
        setInvoiceToDelete(invoice);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (invoiceToDelete) {
            try {
                await InvoiceService.deleteInvoice(invoiceToDelete.invoiceId);
                setData(prevData => prevData.filter(invoice => invoice.invoiceId !== invoiceToDelete.invoiceId));
                handleNotify("Invoice deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
                setShowModal(false);
            } catch (error) {
                handleNotify(error.message || 'Error while deleting invoice.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    };
    // const handleDeleteConfirm = async () => {
    //     if (invoiceToDelete) {
    //         try {
    //             await InvoiceService.deleteInvoice(invoiceToDelete.invoiceId);
    //             const newData = data.filter(invoice => invoice.invoiceId !== invoiceToDelete.invoiceId);
    //             setData(newData);

    //             const newFilteredData = filterData(newData);
    //             const newTotalPages = Math.ceil(newFilteredData.length / rowsPerPage);

    //             if (newFilteredData.length % rowsPerPage === 0 && currentPage > 1) {
    //                 setCurrentPage(currentPage - 1);
    //             } else if (currentPage > newTotalPages && newTotalPages > 0) {
    //                 setCurrentPage(newTotalPages);
    //             }

    //             setShowModal(false);
    //         } catch (error) {
    //             handleNotify(error.message || 'Error while deleting invoice.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
    //         }
    //     }
    // };

    const handleAddNewClick = () => {
        navigate('/invoice/create');
    };

    // Columns for DataTable
    const columns = [
        {
            name: "Invoice#",
            selector: (row) => row.prefixNumber,
            sortable: true,
            width: '10%',
        },
        {
            name: "Subject",
            selector: (row) => row.invoiceSubject,
            sortable: true,
            width: '10%',
        },
        {
            name: "Customer Name",
            selector: (row) => row.lead?.companyName,
            sortable: true,
            width: '15%',
        },
        {
            name: "Total(₹)",
            selector: (row) => row.total,
            sortable: true,
            width: '10%',
        },
        // {
        //     name: "Date",
        //     selector: (row) => {
        //         return (
        //             <>
        //                 <div className="d-flex">{row.validity}</div>
        //                 <div>{row.createDate || ''}</div>
        //                 <div>{row.expirationDate || ''}</div>
        //             </>
        //         )
        //     },
        //     // selector: row => row.expirationDate || '',
        //     sortable: true,
        //     width: '15%',
        // },
        {
            name: "Date",
            selector: (row) => {
                return (
                    <>
                        <div className="d-flex">{`Validity: ${row.validity}`}</div>
                        <div>{`Create Date: ${row.createDate}`}</div>
                        <div className="d-flex">{`Valid Till: ${row.expirationDate}`}</div>
                    </>
                );
            },
            sortable: true,
            width: "20%",
        },
        {
            name: "Status",
            selector: (row) => row.status?.statusName,
            sortable: true,
            width: '15%',
            cell: (row) => {
                const { backgroundColor, textColor } = getButtonStyles(row.status);

                return (
                    <div className="d-flex align-items-center justify-content-between">
                        <button
                            className="btn btn-sm"
                            style={{
                                padding: "6px 12px",
                                fontSize: "14px",
                                borderRadius: "25px",
                                backgroundColor: backgroundColor,
                                color: textColor,
                            }}
                        >
                            {row.status?.statusName}
                        </button>
                    </div>
                );
            },
        },
        {
            name: "Assigned To",
            selector: (row) => {
                return row.user?.username && row.user?.role?.roleName
                    ? `${row.user.username} (${row.user.role.roleName})`
                    : "";
            },
            sortable: true,
            width: '15%',
        },
        {
            name: "Approved",
            width: '15%',
            selector: row => (
                <>
                    <div className={`d-flex ${row.user?.approvalForInvoice ? "text-success" : "text-danger"}`}>
                        {row.user?.approvalForInvoice ? "Approved" : "Unapproved"}
                    </div>
                    <div>
                        {row.user?.username && row.user?.role?.roleName
                            ? `${row.user.username} (${row.user.role.roleName})`
                            : ""}
                    </div>
                    <div className="d-flex">
                        {row.createDate
                            ? `Created on: ${moment(row.createDate).format('MM-DD-YYYY')}`
                            : ""}
                    </div>
                </>
            ),
        },
        {
            name: "Actions",
            button: true,
            width: "15%",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>
                        Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    const handleClickStatusCard = (statusName) => {
        setSelectedStatusCard(statusName);
        setInvoiceTitle(`${statusName} Invoice`);

        const status = data.find(invoice => invoice.status?.statusName === statusName);
        const { backgroundColor } = getButtonStyles({ colorId: status?.status?.colorId });

        if (backgroundColor) {
            setInvoiceTitleColor(backgroundColor);
        } else {
            console.log("Background color not found for status:", statusName);
        }
    }
    useEffect(() => {
        const fetchInvoiceData = async () => {
            try {
                setLoading(true);
                const response = await InvoiceService.getAllInvoices(selectedStore);
                setData(response);
            } catch (error) {
                console.error("Error fetching Invoice:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchInvoiceData();
    }, [selectedStore]);

    const invoiceByStatus = data.reduce((acc, inv) => {
        const statusName = inv.status?.statusName || "Unknown";
        if (!acc[statusName]) {
            acc[statusName] = { count: 0, value: 0 };
        }
        acc[statusName].count += 1;
        acc[statusName].value += inv.invoiceValue || inv.total || 0;
        return acc;
    }, {});

    const totalInvoice = data.length;

    const approvedCount = data.filter(inv => inv.user?.approvalForInvoice).length;
    const unapprovedCount = totalInvoice - approvedCount;

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4" style={{ color: invoiceTitleColor }}>
                        {invoiceTitle}
                    </div>
                    <div className="ms-auto">
                        <button className="btn btn-primary" onClick={handleAddNewClick}>
                            New +
                        </button>
                    </div>
                </div>

                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6 g-4">
                    {Object.keys(invoiceByStatus).map(statusName => {
                        const { count, value } = invoiceByStatus[statusName];
                        const percentage = ((count / totalInvoice) * 100).toFixed(2);

                        // dynamic colors from status.colorId
                        const { backgroundColor } = getButtonStyles({
                            colorId: data.find(inv => inv.status?.statusName === statusName)?.status?.colorId
                        });

                        return (
                            <div key={statusName} className="col" onClick={() => handleClickStatusCard(statusName)}>
                                <div className="card radius-10" style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                    <div className="card-body">
                                        <h5 className="d-flex" style={{ color: backgroundColor }}>{statusName}</h5>
                                        <h5 className="d-flex">₹ {value?.toFixed(2)}</h5>
                                        <div className="d-flex justify-content-between">
                                            <p className="mb-0 font-13 text-primary">{count} Invoice</p>
                                            <p className="mb-0 font-13 text-primary">{percentage}%</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {/* Approved / Unapproved Card */}
                    <div className="col">
                        <div className="card radius-10 border-start border-0 border-4 border-primary">
                            <div className="card-body">
                                <h5 className="d-flex text-primary">Approved / Unapproved</h5>
                                <h5 className="">{approvedCount} / {unapprovedCount}</h5>
                                <div className="d-flex justify-content-between">
                                    <p className="mb-0 font-13 text-primary">{approvedCount} Approved</p>
                                    <p className="mb-0 font-13 text-primary">{unapprovedCount} Unapproved</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card radius-10 border-top border-0 border-4 border-primary mt-3 p-4">
                    <div className="card-body">
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>

                            <div className="d-flex ms-auto align-items-center">
                                <div className="me-2">
                                    <span className="fw-bold">Search:</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control w-100 w-md-auto"
                                    placeholder="Search 🔍..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="table-responsive">
                            <DataTable
                                columns={columns}
                                data={currentData}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>

                        {showModal && (
                            <DeleteModelPopupComponent
                                show={showModal}
                                onClose={() => setShowModal(false)}
                                onConfirm={handleDeleteConfirm}
                                title="Confirm"
                                confirmText="Delete"
                                Message="Are you sure you want to delete this invoices?"
                            />
                        )}

                        {/* Pagination Controls */}
                        <div className="d-flex mt-4 justify-content-end">
                            <div className="btn-group">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </button>
                                {Array.from({ length: totalPages }, (_, index) => (
                                    <button
                                        key={index}
                                        className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceComponent;